import React, {forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState} from "react";

import {OpenLayers} from "@delivus-daas/react-open-layers";
import {DEFAULT_MAP_OPTIONS, MAX_ZOOM, MIN_ZOOM, SEOUL_CENTER, ZOOM} from "shared/constants/consts.common";
import {useToast} from "shared/ui";
import {IPlanLongItem, IShippingWithCluster, MapSearchParamKey} from "features/plan/types";
import {PlanMapControls} from "./PlanMapControls";
import {Map, MapEvent} from "ol";
import debounce from "lodash.debounce";
import {useSearchParams} from "react-router-dom";
import TileLayer from "ol/layer/Tile";
import {XYZ} from "ol/source";
import {useZoomSlider} from "./useZoomSlider";

interface Props {
  selectedPlans: IPlanLongItem[];
  onSelectShippings: (shippings: IShippingWithCluster[]) => void;
  onRefresh: () => void;
}

export interface PlanMapRef {
  resetSelectedShippings: () => void;
}

const PlanMap = forwardRef<PlanMapRef, Props>(({onSelectShippings, selectedPlans, onRefresh}: Props, ref) => {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const paramsRef = useRef<URLSearchParams>();
  const lastSavedMapStateRef = useRef("");
  const [map, setMap] = useState<Map>();
  const [bound, setBound] = useState<number[]>();
  
  useEffect(() => {
    paramsRef.current = searchParams
    lastSavedMapStateRef.current = [
      searchParams.get(MapSearchParamKey.mapCenterLng) ?? "",
      searchParams.get(MapSearchParamKey.mapCenterLat) ?? "",
      searchParams.get(MapSearchParamKey.mapZoom) ?? "",
    ].join("|");
  }, [searchParams]);
  
  const vworldBaseLayer = useMemo(() => new TileLayer({
    source: new XYZ({
      url: `http://api.vworld.kr/req/wmts/1.0.0/${process.env.REACT_APP_MAP_KEY}/white/{z}/{y}/{x}.png`,
    }), properties: {name: "base-vworld-base"}, preload: Infinity,
  }), []);
  
  const center = useMemo(() => {
    const latValue = searchParams.get(MapSearchParamKey.mapCenterLat);
    const lngValue = searchParams.get(MapSearchParamKey.mapCenterLng);

    if (latValue !== null && lngValue !== null) {
      const lat = Number(latValue);
      const lng = Number(lngValue);

      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return [lng, lat];
      }
    }

    return SEOUL_CENTER;
  }, [searchParams]);
  
  const zoom = useMemo(() => {
    const mapZoomValue = searchParams.get(MapSearchParamKey.mapZoom);

    if (mapZoomValue !== null) {
      const mapZoom = Number(mapZoomValue);

      if (Number.isFinite(mapZoom)) {
        return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, mapZoom));
      }
    }

    return ZOOM;
  }, [searchParams]);
  
  useZoomSlider(map);
  
  const syncMapSearchParams = useMemo(() => debounce((currentMap: Map) => {
    const view = currentMap.getView();
    const nextCenter = view.getCenter() as number[] | undefined;
    const nextZoom = view.getZoom();
    
    if (!nextCenter || !Number.isFinite(nextCenter[0]) || !Number.isFinite(nextCenter[1]) || !Number.isFinite(nextZoom)) {
      return;
    }
    
    const nextLng = nextCenter[0].toFixed(4);
    const nextLat = nextCenter[1].toFixed(4);
    const nextZoomValue = String(Math.round((nextZoom ?? ZOOM) * 100) / 100);
    const nextMapStateKey = [nextLng, nextLat, nextZoomValue].join("|");
    
    if (lastSavedMapStateRef.current === nextMapStateKey) {
      return;
    }
    
    lastSavedMapStateRef.current = nextMapStateKey;
    
    setSearchParams((prev) => {
      const params = new URLSearchParams(paramsRef.current ?? prev);
      params.set(MapSearchParamKey.mapCenterLng, nextLng);
      params.set(MapSearchParamKey.mapCenterLat, nextLat);
      params.set(MapSearchParamKey.mapZoom, nextZoomValue);
      return params;
    }, {replace: true});
  }, 600), [setSearchParams]);
  
  useEffect(() => {
    return () => {
      syncMapSearchParams.cancel();
    };
  }, [syncMapSearchParams]);
  
  const handleMapInit = useCallback((nextMap: Map) => {
    setMap(nextMap);
    setBound(nextMap.getView()
                    .calculateExtent(nextMap.getSize()));
  }, []);
  
  const handleMapBoundChanged = useCallback((event: MapEvent) => {
    const currentMap = event.map;
    
    if (!currentMap) {
      return;
    }
    
    setBound(currentMap.getView()
                       .calculateExtent(currentMap.getSize()));
    syncMapSearchParams(currentMap);
  }, [syncMapSearchParams]);
  
  return (<div className={"map-container"}>
    <OpenLayers
      initialLayers={[vworldBaseLayer]}
      initialInteractionOptions={{pinchZoom: true}}
      onMoveEnd={handleMapBoundChanged}
      onInit={handleMapInit}
      initialViewOptions={{zoom, center: center ?? SEOUL_CENTER, ...DEFAULT_MAP_OPTIONS}}
    >
      <PlanMapControls
        ref={ref}
        map={map}
        bound={bound}
        zoom={zoom}
        selectedPlans={selectedPlans}
        onRefresh={onRefresh}
        onSelectShippings={onSelectShippings}
      />
    </OpenLayers>
  </div>);
});

export default memo(PlanMap);
